<script lang="ts" setup>
	import { createReusableTemplate } from '@vueuse/core'
	import type { PricingCardPattern } from '~/contents/pricing'
	import StringMethods from '~/utils/character'
	export type PricingCardTitleContext = Partial<{
		base: any
		font: string
		size: string
		transition: string
		hover: string
		text: string
	}>
	export type PricingCardDescription = Partial<{
		base: string
		size: string
		font: string
		text: string
		width: string
	}>
	const descriptionConfig: PricingCardDescription = {
		base: '',
		size: 'text-sm ',
		font: 'font-medium',
		text: 'text-gray-600',
		width: 'max-w-[310px]'
	}
	const titleConfig: PricingCardTitleContext = {
		base: ' capitalize',
		font: 'font-semibold',
		size: 'sm:text-xl text-lg',
		hover: 'group-hover:sm:text-[24px]',
		transition: 'custom-transition',
		text: ''
	}

	const configClass = {
		wrapper: {
			base: 'relative flex-none flex flex-col justify-between item-start',
			rounded: 'rounded-lg',
			bg: 'bg-white',
			border: 'border',
			transition: 'custom-transition-2',
			hover: 'hover:bg-purple-50 hover:border-purple-300',
			height: 'min-h-[590px]',
			width: 'max-w-[440px] sm:min-w-[380px] min-w-[325.36px] w-full',
			padding: 'p-5'
		},
		heading: {
			border: 'border-b border-gray-300 relative',
			spacer: 'space-y-4',
			paddingBlock: 'sm:py-5 ',
			base: ''
		},
		list: {
			base: 'sm:py-5  space-y-3 mb-auto relative z-10 overflow-hidden',
			items: 'flex items-center gap-x-3 ',
			iconHover: 'group-hover:fill-main ',
			iconTransition: 'custom-transition-3',
			text: 'text-gray-500',
			size: 'sm:text-base text-sm'
		},
		actionBtn: {
			base: 'underline-2',
			padding: 'pt-10  pl-0',
			paddingBlock: 'pt-8',
			zIndex: 'z-10',
			hover: 'group-hover:text-main ',
			text: 'sm:text-lg text-gray-700',
			display: 'inline-flex'
		}
	}

	const { wrapper, list, heading, actionBtn } = configClass
	const { btnLabel = 'Discover More' } = defineProps<
		PricingCardPattern & {
			ui?: {
				wrapper?: Partial<{
					base: string
					rounded: string
					bg: string
					border: string
					transition: string
					hover: string
					height: string
					width: string
					padding: string
				}>
				heading?: Partial<{
					border: string
					spacer: string
					paddingBlock: string
					base: string
					name: Partial<PricingCardTitleContext>
					description: Partial<PricingCardDescription>
				}>
				list?: Partial<{
					base: string
					default: string
					items: string
					iconHover: string
					iconTransition: string
					text: string
					size: string
					iconBase: string
					iconHeight: string
				}>
				actionBtn?: Partial<{
					base: string
					padding: string
					paddingBlock: string
					zIndex: string
					text: string
					display: string
					hover?: string
				}>
			}
			noIcon?: boolean
			label?: string
			btnLabel?: string
		}
	>()
	const { boldText } = StringMethods()
	const [defineTemplate, reuseTemplate] = createReusableTemplate<{ item: PricingCardPattern['benefit_highlight'] }>()
	defineEmits<{
		(event: 'learnMore'): void
	}>()
</script>

<template>
	<div
		class="group"
		:class="
			cn(
				ui?.wrapper?.base ?? wrapper.base,
				ui?.wrapper?.bg ?? wrapper.bg,
				ui?.wrapper?.border ?? wrapper.border,
				ui?.wrapper?.height ?? wrapper.height,
				ui?.wrapper?.hover ?? wrapper.hover,
				ui?.wrapper?.padding ?? wrapper.padding,
				ui?.wrapper?.rounded ?? wrapper.rounded,
				ui?.wrapper?.transition ?? wrapper.transition,
				ui?.wrapper?.width ?? wrapper.width
			)
		"
	>
		<slot name="header">
			<div
				:class="
					cn(
						'relative z-[70]',
						ui?.heading?.base ?? heading?.base,
						ui?.heading?.border ?? heading.border,
						ui?.heading?.paddingBlock ?? heading.paddingBlock,
						ui?.heading?.spacer ?? heading.spacer
					)
				"
			>
				<h3
					:class="
						cn(
							ui?.heading?.name?.base ?? name?.ui?.base ?? titleConfig?.base,
							ui?.heading?.name?.font ?? name?.ui?.font ?? titleConfig?.font,
							ui?.heading?.name?.hover ?? name?.ui?.hover ?? titleConfig?.hover,
							ui?.heading?.name?.size ?? name?.ui?.size ?? titleConfig?.size,
							ui?.heading?.name?.text ?? name?.ui?.text ?? titleConfig?.text,
							ui?.heading?.name?.transition ?? name?.ui?.transition ?? titleConfig?.transition
						)
					"
				>
					<slot name="name">
						<span v-html="name?.text"></span>
					</slot>
				</h3>

				<div
					:class="
						cn(
							ui?.heading?.description?.base ?? description?.ui?.base ?? descriptionConfig?.base,
							ui?.heading?.description?.font ?? description?.ui?.font ?? descriptionConfig?.font,
							ui?.heading?.description?.size ?? description?.ui?.size ?? descriptionConfig?.size,
							ui?.heading?.description?.text ?? description?.ui?.text ?? descriptionConfig?.text,
							ui?.heading?.description?.width ?? description?.ui?.width ?? descriptionConfig?.width
						)
					"
				>
					<slot name="description">
						<p v-html="description?.text" />
					</slot>
				</div>
				<p class="font-medium text-sm text-gray-600">
					<slot name="label">
						{{ label }}
					</slot>
				</p>

				<div
					v-for="(t, index) in tag?.text ?? []"
					:key="'tag-' + index"
					:class="
						cn(
							'sm:p-[10px_16px] mx-1 first:ml-0 custom-transition-2 lg:group-hover:bg-purple-500 lg:group-hover:text-white p-[6px_10px] rounded-[20px] bg-purple-5 text-purple-500 font-medium text-xs inline-flex',
							tag?.ui ?? ''
						)
					"
				>
					{{ t }}
				</div>
			</div>
		</slot>
		<defineTemplate v-slot="{ item }">
			<li v-for="(benefit, index) in item" :key="index" :class="ui?.list?.items ?? list?.items">
				<slot name="list-icon" :benefit="benefit">
					<tools-icon
						v-if="!benefit.notAllowed"
						dir="fill"
						icon="check"
						:width="14"
						:height="14"
						:path-class="`${ui?.list?.iconHover ?? 'group-hover:fill-base-primary'} ${
							ui?.list?.iconTransition
						} ${ui?.list?.iconBase}`"
						svg-class="sm:h-[24px] sm:w-[24px] h-[14px] w-[14px]"
					/>
					<tools-icon
						v-else
						dir="fill"
						:width="14"
						:height="14"
						icon="not-allowed"
						:path-class="`${ui?.list?.iconHover ?? 'group-hover:fill-error-500'} ${
							ui?.list?.iconTransition
						} `"
						:svg-class="ui?.list?.iconHeight ?? 'sm:h-[24px] sm:w-[24px] h-[14px] w-[14px]'"
					/>
				</slot>
				<span
					:class="[ui?.list?.size ?? list?.size, ui?.list?.text ?? list?.text]"
					v-html="boldText(benefit?.text ?? '', 'text-[#3C3C3C]')"
				/>
			</li>
		</defineTemplate>

		<p v-if="individualTag" class="text-gray-500 text-base relative z-10 tracking-wide" v-html="individualTag" />

		<ul :class="cn('relative z-10 mt-3 sm:mt-0', ui?.list?.base ?? list?.base, ui?.list?.default)">
			<reuseTemplate v-if="benefit_highlight?.length" :item="benefit_highlight" />
			<reuseTemplate v-else-if="individual_benefit?.length" :item="individual_benefit" />
			<reuseTemplate v-else-if="dedicated_benfit?.length" :item="dedicated_benfit" />
		</ul>

		<div :class="cn(ui?.actionBtn?.paddingBlock ?? actionBtn?.paddingBlock)">
			<slot
				name="btn-group"
				:btnGroupClass="
					cn(
						'group/item',
						'z-10 items-center gap-x-2',
						ui?.actionBtn?.base ?? actionBtn?.base,
						ui?.actionBtn?.display ?? actionBtn?.display,
						ui?.actionBtn?.padding ?? actionBtn?.padding,
						ui?.actionBtn?.text ?? actionBtn?.text,
						ui?.actionBtn?.zIndex ?? actionBtn?.zIndex,
						ui?.actionBtn?.hover ?? actionBtn?.hover
					)
				"
			>
				<button
					:class="
						cn(
							'group/item',
							ui?.actionBtn?.base ?? actionBtn?.base,
							ui?.actionBtn?.display ?? actionBtn?.display,
							ui?.actionBtn?.padding ?? actionBtn?.padding,
							ui?.actionBtn?.text ?? actionBtn?.text,
							ui?.actionBtn?.zIndex ?? actionBtn?.zIndex,
							ui?.actionBtn?.hover ?? actionBtn?.hover
						)
					"
					name="discover more"
					:aria-label="btnLabel"
					type="button"
					class="z-10 items-center gap-x-2"
					@click="$emit('learnMore')"
				>
					<slot
						name="buttn"
						:btnGroupClass="
							cn(
								'group/item',
								ui?.actionBtn?.base ?? actionBtn?.base,
								ui?.actionBtn?.display ?? actionBtn?.display,
								ui?.actionBtn?.padding ?? actionBtn?.padding,
								ui?.actionBtn?.text ?? actionBtn?.text,
								ui?.actionBtn?.zIndex ?? actionBtn?.zIndex,
								ui?.actionBtn?.hover ?? actionBtn?.hover
							)
						"
					>
						{{ btnLabel }}
						<span class="custom-transition-3 group-hover/item:translate-x-2">
							<tools-icon
								dir="arrows"
								icon="arrow-short-r"
								:height="16"
								:width="16"
								path-class="fill-main sm:fill-gray-700 group-hover/item:fill-main stroke-[1px]"
							/>
						</span>
					</slot>
				</button>
			</slot>
		</div>

		<slot name="footer">
			<div v-show="!noIcon" class="absolute right-0 bottom-0">
				<span class="inline custom-transition-2 group-hover:hidden">
					<tools-icon
						dir="product-icons"
						:icon="card_icon?.default?.icon ?? 'zencare-heart'"
						width="180"
						height="137"
						viewBox="0 0 180 137"
					/>
				</span>
				<span class="hidden custom-transition-2 group-hover:inline">
					<tools-icon
						dir="product-icons"
						:icon="card_icon?.onhover?.icon ?? 'zencare-heart-filled'"
						width="180"
						height="137"
						viewBox="0 0 180 137"
					/>
				</span>
			</div>
		</slot>
	</div>
</template>
